<template>
  <main class="core-page core-page--wide liquor-ordering-view">
    <div class="card">
      <div class="card-content">
        <span class="card-title"> {{ supplierName }} Liquor Ordering </span>

        <p class="grey-text text-darken-1 intro-copy">
          Enter the number of Order Units required. CoreDeck converts the ordered quantity
          into Stock Units using the conversion stored against each product in the Stock
          Database.
        </p>

        <div
          v-if="!activeVenueId"
          class="card-panel amber lighten-4 amber-text text-darken-4"
        >
          No venue is currently selected. Please select a venue from Venue Snapshot before
          submitting an order.
        </div>

        <!-- =====================================================
             WEEKLY LIQUOR ORDERING BUDGET
             ===================================================== -->

        <section class="budget-panel" aria-labelledby="liquor-budget-title">
          <div class="budget-heading">
            <div>
              <h6 id="liquor-budget-title">Weekly Liquor Ordering Budget</h6>

              <p>
                Previous week's actual liquor sales

                <span v-if="budget.previousWeekEnding">
                  (W/E
                  {{ formatShortDate(budget.previousWeekEnding) }})
                </span>
              </p>
            </div>

            <button
              type="button"
              class="btn-flat teal-text text-darken-2 budget-refresh"
              :disabled="budgetLoading || !activeVenueId"
              @click="loadBudget"
            >
              <i class="material-icons left"> refresh </i>

              Refresh
            </button>
          </div>

          <div v-if="budgetLoading" class="progress budget-progress">
            <div class="indeterminate"></div>
          </div>

          <div
            v-else-if="budgetError"
            class="card-panel red lighten-4 red-text text-darken-4 budget-error"
          >
            {{ budgetError }}
          </div>

          <template v-else>
            <div class="budget-grid">
              <div class="budget-tile">
                <span> Previous Week Liquor Sales </span>

                <strong>
                  {{ money(budget.previousWeekLiquorSales) }}
                </strong>
              </div>

              <div class="budget-tile budget-limit">
                <span> Weekly Budget </span>

                <strong>
                  {{ money(budget.weeklyBudget) }}
                </strong>
              </div>

              <div class="budget-tile">
                <span> Orders This Week </span>

                <strong>
                  {{ money(budget.ordersThisWeek) }}
                </strong>
              </div>

              <div
                class="budget-tile"
                :class="{
                  'budget-positive': budget.remainingBudget >= 0,

                  'budget-negative': budget.remainingBudget < 0,
                }"
              >
                <span> Remaining Budget </span>

                <strong>
                  {{ money(budget.remainingBudget) }}
                </strong>
              </div>
            </div>

            <div
              v-if="selectedItems.length"
              class="projected-budget-line"
              :class="{
                overspend: projectedRemainingBudget < 0,
              }"
            >
              <span>
                After this
                {{ supplierName }}
                order
              </span>

              <strong>
                {{ money(projectedRemainingBudget) }}
              </strong>
            </div>

            <p
              v-if="budget.previousWeekLiquorSales === 0"
              class="budget-zero-warning amber-text text-darken-4"
            >
              No previous-week liquor X-Read sales have been recorded for this venue, so
              the calculated ordering budget is £0.00.
            </p>
          </template>
        </section>

        <!-- =====================================================
             PRODUCT LOADING
             ===================================================== -->

        <div v-if="loadingProducts" class="loading-panel">
          <div class="preloader-wrapper small active">
            <div class="spinner-layer spinner-teal-only">
              <div class="circle-clipper left">
                <div class="circle"></div>
              </div>

              <div class="gap-patch">
                <div class="circle"></div>
              </div>

              <div class="circle-clipper right">
                <div class="circle"></div>
              </div>
            </div>
          </div>

          <span>
            Loading
            {{ supplierName }}
            products...
          </span>
        </div>

        <div
          v-else-if="loadError"
          class="card-panel red lighten-4 red-text text-darken-4"
        >
          {{ loadError }}

          <button
            type="button"
            class="btn-flat red-text text-darken-2"
            @click="loadProducts(true)"
          >
            Try Again
          </button>
        </div>

        <div
          v-else-if="!products.length"
          class="card-panel blue lighten-5 blue-text text-darken-4"
        >
          No stock database lines were found for
          <strong> {{ supplierName }} </strong>. Check that the relevant stock items have
          their Supplier field set to "{{ supplierName }}".
        </div>

        <!-- =====================================================
             ORDER FORM
             ===================================================== -->

        <form v-else @submit.prevent="submitOrder">
          <div class="order-summary-panel">
            <div class="summary-tile">
              <span> Selected Lines </span>

              <strong>
                {{ selectedItems.length }}
              </strong>
            </div>

            <div class="summary-tile">
              <span> Total Order Units </span>

              <strong>
                {{ totalQuantity }}
              </strong>
            </div>

            <div class="summary-tile total">
              <span> Order Total </span>

              <strong>
                {{ money(orderTotal) }}
              </strong>
            </div>
          </div>

          <div v-if="unconfiguredProducts.length" class="configuration-warning">
            <i class="material-icons"> warning </i>

            <span>
              {{ unconfiguredProducts.length }}
              product{{ unconfiguredProducts.length === 1 ? "" : "s" }}
              still
              {{ unconfiguredProducts.length === 1 ? "needs" : "need" }}
              the Order Unit / Stock Unit / Sales Unit conversion completing in the Stock
              Database.
            </span>
          </div>

          <div class="category-tabs">
            <button
              v-for="category in categoryTabs"
              :key="category"
              type="button"
              class="category-tab"
              :class="{
                active: activeCategory === category,
              }"
              @click="activeCategory = category"
            >
              {{ category }}
            </button>
          </div>

          <div class="product-table-wrap">
            <table class="striped product-table">
              <thead>
                <tr>
                  <th>Category</th>

                  <th>Product</th>

                  <th>Order Unit</th>

                  <th>Stock Unit</th>

                  <th class="center-align">Stock / Order</th>

                  <th class="right-align">Order Unit Cost</th>

                  <th class="center-align quantity-heading">Order Qty</th>

                  <th class="right-align">Stock In</th>

                  <th class="right-align">Line Total</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="item in visibleProducts"
                  :key="item.id"
                  :class="{
                    'conversion-missing': !conversionConfigured(item),
                  }"
                >
                  <td>
                    <span class="category-pill">
                      {{ item.category }}
                    </span>
                  </td>

                  <td class="product-name">
                    {{ item.product }}
                  </td>

                  <td>
                    {{ item.orderUnit || "—" }}
                  </td>

                  <td>
                    {{ item.stockUnit || "—" }}
                  </td>

                  <td class="center-align">
                    {{ displayNumber(item.stockUnitsPerOrderUnit) }}
                  </td>

                  <td class="right-align">
                    {{ money(item.price) }}
                  </td>

                  <td class="quantity-cell">
                    <input
                      :id="`qty-${item.id}`"
                      v-model.number="quantities[item.id]"
                      type="number"
                      min="0"
                      step="1"
                      inputmode="numeric"
                      class="quantity-input"
                      :disabled="!conversionConfigured(item)"
                      :aria-label="`Order quantity for ${item.product}`"
                    />
                  </td>

                  <td class="right-align stock-in-value">
                    <template v-if="conversionConfigured(item)">
                      {{ displayNumber(stockEquivalent(item)) }}
                      {{ item.stockUnit }}
                    </template>

                    <span v-else class="not-configured"> Not configured </span>
                  </td>

                  <td class="right-align line-total">
                    {{ money(lineTotal(item)) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="selectedItems.length" class="selected-order-panel">
            <h6 class="section-heading compact">
              Selected
              {{ supplierName }}
              Order
            </h6>

            <div class="selected-table-wrap">
              <table class="striped selected-table">
                <thead>
                  <tr>
                    <th>Product</th>

                    <th>Order Unit</th>

                    <th>Stock Unit</th>

                    <th class="center-align">Qty</th>

                    <th class="right-align">Stock In</th>

                    <th class="right-align">Line Total</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="item in selectedItems" :key="item.id">
                    <td>
                      {{ item.product }}
                    </td>

                    <td>
                      {{ item.orderUnit }}
                    </td>

                    <td>
                      {{ item.stockUnit }}
                    </td>

                    <td class="center-align">
                      {{ item.quantity }}
                    </td>

                    <td class="right-align">
                      {{ displayNumber(item.stockQuantity) }}
                      {{ item.stockUnit }}
                    </td>

                    <td class="right-align">
                      {{ money(item.lineTotal) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="form-actions">
            <button
              class="btn teal darken-2 waves-effect waves-light"
              type="submit"
              :disabled="!canSubmit"
            >
              <i class="material-icons left"> send </i>

              {{ submitting ? "Submitting..." : "Submit Order for Approval" }}
            </button>

            <button
              class="btn-flat"
              type="button"
              :disabled="submitting"
              @click="resetForm"
            >
              Reset
            </button>

            <button
              class="btn-flat teal-text text-darken-2"
              type="button"
              :disabled="loadingProducts || submitting"
              @click="loadProducts(true)"
            >
              Refresh Products
            </button>
          </div>

          <p v-if="successMessage" class="green-text text-darken-2">
            {{ successMessage }}
          </p>

          <p v-if="errorMessage" class="red-text text-darken-2">
            {{ errorMessage }}
          </p>
        </form>
      </div>
    </div>

    <!-- =====================================================
         OVERSPEND REASON MODAL
         ===================================================== -->

    <div
      v-if="overspendModalOpen"
      class="overspend-modal-backdrop"
      @click.self="closeOverspendModal"
    >
      <section
        class="overspend-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="overspend-modal-title"
      >
        <div class="overspend-modal-header">
          <div>
            <h5 id="overspend-modal-title">Weekly Liquor Budget Exceeded</h5>

            <p>
              This order would take the venue above its weekly liquor ordering budget.
            </p>
          </div>

          <button
            type="button"
            class="overspend-close"
            aria-label="Close overspend reason form"
            :disabled="submitting"
            @click="closeOverspendModal"
          >
            <i class="material-icons"> close </i>
          </button>
        </div>

        <div class="overspend-values">
          <div>
            <span> Weekly Budget </span>

            <strong>
              {{ money(budget.weeklyBudget) }}
            </strong>
          </div>

          <div>
            <span> Orders Already Submitted </span>

            <strong>
              {{ money(budget.ordersThisWeek) }}
            </strong>
          </div>

          <div>
            <span> This Order </span>

            <strong>
              {{ money(orderTotal) }}
            </strong>
          </div>

          <div class="overspend-value-red">
            <span> Projected Overspend </span>

            <strong>
              {{ money(projectedOverspend) }}
            </strong>
          </div>
        </div>

        <div class="input-field overspend-reason-field">
          <textarea
            id="overspend-reason"
            v-model="overspendReason"
            class="materialize-textarea"
            :disabled="submitting"
            :maxlength="1000"
            autofocus
          ></textarea>

          <label for="overspend-reason" class="active"> Reason for overspend </label>
        </div>

        <p
          class="reason-requirement"
          :class="{
            valid: overspendReasonValid,
          }"
        >
          Minimum
          {{ overspendMinimumCharacters }}
          characters required.

          {{ overspendReason.trim().length }}/{{ overspendMinimumCharacters }}
        </p>

        <div class="overspend-modal-actions">
          <button
            type="button"
            class="btn-flat"
            :disabled="submitting"
            @click="closeOverspendModal"
          >
            Cancel
          </button>

          <button
            type="button"
            class="btn red darken-2 waves-effect waves-light"
            :disabled="submitting || !overspendReasonValid"
            @click="confirmOverspendSubmission"
          >
            <i class="material-icons left"> warning </i>

            {{ submitting ? "Submitting..." : "Submit with Reason" }}
          </button>
        </div>
      </section>
    </div>
  </main>
</template>

<script setup>
import { computed, reactive, ref, unref, watch } from "vue";

import { getFunctions, httpsCallable } from "firebase/functions";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

import { fetchStockUnitsForSupplier } from "@/scripts/sqlApiHelper/stockUnitsApi";

const props = defineProps({
  currentUserProfile: {
    type: Object,
    default: () => ({}),
  },

  supplierName: {
    type: String,
    required: true,
  },

  supplierEmail: {
    type: String,
    default: "",
  },
});

const venueContext = useVenueContext();

const { venueId } = venueContext;

const functions = getFunctions(undefined, "us-central1");

const submitLiquorOrderCallable = httpsCallable(functions, "submitLiquorOrder");

const FROM_EMAIL = "matthew@stonegrouplimited.com";

const stockRows = ref([]);
const quantities = reactive({});
const productCache = new Map();

let loadSequence = 0;
let budgetSequence = 0;

const activeCategory = ref("All");

const loadingProducts = ref(false);

const submitting = ref(false);

const loadError = ref("");

const successMessage = ref("");

const errorMessage = ref("");

const budgetLoading = ref(false);

const budgetError = ref("");

const budget = reactive({
  weekEnding: "",
  previousWeekEnding: "",
  previousWeekLiquorSales: 0,
  budgetRate: 0.28,
  budgetPercent: 28,
  weeklyBudget: 0,
  ordersThisWeek: 0,
  remainingBudget: 0,
});

const overspendModalOpen = ref(false);

const overspendReason = ref("");

const overspendMinimumCharacters = ref(3);

const activeVenueId = computed(() => String(unref(venueId) || "").trim());

const activeWeekEnding = computed(() => {
  const supplied =
    unref(venueContext.weekEnding) ||
    unref(venueContext.weekEndingDate) ||
    unref(venueContext.weekEndingISO) ||
    unref(venueContext.week_ending_date) ||
    "";

  const normalised = normaliseIsoDate(supplied);

  if (normalised) {
    return weekEndingFromIso(normalised);
  }

  const tradingDate = normaliseIsoDate(
    unref(venueContext.tradingDate) ||
      unref(venueContext.trading_date) ||
      unref(venueContext.businessDate) ||
      ""
  );

  if (tradingDate) {
    return weekEndingFromIso(tradingDate);
  }

  return weekEndingFromIso(toLocalIsoDate(new Date()));
});

const products = computed(() => {
  return stockRows.value
    .filter((row) => String(row.item_name || "").trim())
    .map((row) => ({
      id: row.id,

      category: String(row.product_group || "").trim() || "Other",

      product: String(row.item_name || "").trim(),

      price: cleanMoney(row.unit_cost),

      orderUnit: String(row.order_unit || "").trim(),

      stockUnit: String(row.stock_unit || "").trim(),

      stockUnitsPerOrderUnit: cleanNumber(row.stock_units_per_order_unit),

      salesUnitsPerStockUnit: cleanNumber(row.sales_units_per_stock_unit),

      supplier: String(row.supplier || "").trim(),
    }))
    .sort((a, b) => {
      const categoryComparison = a.category.localeCompare(b.category, undefined, {
        sensitivity: "base",

        numeric: true,
      });

      if (categoryComparison !== 0) {
        return categoryComparison;
      }

      return a.product.localeCompare(b.product, undefined, {
        sensitivity: "base",

        numeric: true,
      });
    });
});

const unconfiguredProducts = computed(() =>
  products.value.filter((item) => !conversionConfigured(item))
);

const categoryTabs = computed(() => [
  "All",

  ...Array.from(new Set(products.value.map((item) => item.category))),
]);

const visibleProducts = computed(() => {
  if (activeCategory.value === "All") {
    return products.value;
  }

  return products.value.filter((item) => item.category === activeCategory.value);
});

const selectedItems = computed(() => {
  return products.value
    .map((item) => {
      const quantity = cleanQuantity(quantities[item.id]);

      return {
        ...item,

        quantity,

        stockQuantity: quantity * item.stockUnitsPerOrderUnit,

        lineTotal: roundMoney(quantity * item.price),
      };
    })
    .filter((item) => item.quantity > 0);
});

const totalQuantity = computed(() => {
  return selectedItems.value.reduce(
    (total, item) => total + item.quantity,

    0
  );
});

const orderTotal = computed(() => {
  return roundMoney(
    selectedItems.value.reduce(
      (total, item) => total + item.lineTotal,

      0
    )
  );
});

const projectedRemainingBudget = computed(() => {
  return roundMoney(budget.remainingBudget - orderTotal.value);
});

const projectedOverspend = computed(() => {
  return roundMoney(Math.max(0, -projectedRemainingBudget.value));
});

const overspendReasonValid = computed(() => {
  return overspendReason.value.trim().length >= overspendMinimumCharacters.value;
});

const canSubmit = computed(() => {
  return Boolean(
    !submitting.value &&
      !budgetLoading.value &&
      !budgetError.value &&
      activeVenueId.value &&
      selectedItems.value.length &&
      selectedItems.value.every(conversionConfigured)
  );
});

watch(
  () => props.supplierName,

  async () => {
    activeCategory.value = "All";

    successMessage.value = "";

    errorMessage.value = "";

    closeOverspendModal();

    await loadProducts();
  },

  {
    immediate: true,
  }
);

watch(
  [activeVenueId, activeWeekEnding],

  async () => {
    closeOverspendModal();

    await loadBudget();
  },

  {
    immediate: true,
  }
);

/* =========================================================
   Dates
   ========================================================= */

function normaliseIsoDate(value) {
  const text = String(value || "").trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }

  const date = new Date(text);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return toLocalIsoDate(date);
}

function toLocalIsoDate(date) {
  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-` + `${month}-` + `${day}`;
}

function weekEndingFromIso(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ""))) {
    return "";
  }

  const [year, month, day] = iso.split("-").map(Number);

  const date = new Date(year, month - 1, day);

  const daysUntilSunday = (7 - date.getDay()) % 7;

  date.setDate(date.getDate() + daysUntilSunday);

  return toLocalIsoDate(date);
}

function formatShortDate(iso) {
  if (!iso) {
    return "—";
  }

  const [year, month, day] = String(iso).split("-").map(Number);

  const date = new Date(year, month - 1, day);

  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

/* =========================================================
   Budget
   ========================================================= */

function applyBudget(data = {}) {
  budget.weekEnding = String(data.weekEnding || activeWeekEnding.value || "");

  budget.previousWeekEnding = String(data.previousWeekEnding || "");

  budget.previousWeekLiquorSales = roundMoney(data.previousWeekLiquorSales);

  budget.budgetRate = cleanNumber(data.budgetRate) || 0.28;

  budget.budgetPercent = cleanNumber(data.budgetPercent) || 28;

  budget.weeklyBudget = roundMoney(data.weeklyBudget);

  budget.ordersThisWeek = roundMoney(data.ordersThisWeek);

  budget.remainingBudget = roundMoney(data.remainingBudget);
}

async function loadBudget() {
  const requestSequence = ++budgetSequence;

  budgetError.value = "";

  if (!activeVenueId.value) {
    budgetLoading.value = false;

    applyBudget({});

    return null;
  }

  budgetLoading.value = true;

  try {
    const result = await submitLiquorOrderCallable({
      action: "getBudget",

      venueId: activeVenueId.value,

      weekEnding: activeWeekEnding.value,
    });

    if (requestSequence !== budgetSequence) {
      return null;
    }

    applyBudget(result?.data || {});

    return result?.data || {};
  } catch (error) {
    console.error("[LiquorOrderingForm] Budget load failed:", error);

    if (requestSequence === budgetSequence) {
      budgetError.value =
        error?.details?.message ||
        error?.message ||
        "Unable to load the weekly liquor ordering budget.";
    }

    return null;
  } finally {
    if (requestSequence === budgetSequence) {
      budgetLoading.value = false;
    }
  }
}

/* =========================================================
   Products
   ========================================================= */

async function loadProducts(forceRefresh = false) {
  const supplierName = String(props.supplierName || "").trim();

  const requestSequence = ++loadSequence;

  if (!supplierName) {
    stockRows.value = [];
    loadingProducts.value = false;

    return;
  }

  loadingProducts.value = true;

  loadError.value = "";

  try {
    let result = productCache.get(supplierName);

    if (forceRefresh || !result) {
      result = await fetchStockUnitsForSupplier(supplierName);

      productCache.set(supplierName, Array.isArray(result) ? result : []);
    }

    if (requestSequence !== loadSequence) {
      return;
    }

    stockRows.value = Array.isArray(result) ? result : [];

    stockRows.value.forEach((row) => {
      if (row.id && !Object.prototype.hasOwnProperty.call(quantities, row.id)) {
        quantities[row.id] = 0;
      }
    });
  } catch (error) {
    console.error("[LiquorOrderingForm] Failed to load stock units:", error);

    loadError.value =
      error?.message || "The stock database could not be loaded. Please try again.";
  } finally {
    if (requestSequence === loadSequence) {
      loadingProducts.value = false;
    }
  }
}

/* =========================================================
   Number / display helpers
   ========================================================= */

function cleanNumber(value) {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
}

function cleanMoney(value) {
  const number = Number(
    String(value ?? "")
      .replace(/£/g, "")
      .replace(/,/g, "")
      .trim()
  );

  return Number.isFinite(number) ? roundMoney(number) : 0;
}

function cleanQuantity(value) {
  const number = Number(value);

  if (!Number.isFinite(number) || number < 0) {
    return 0;
  }

  return Math.floor(number);
}

function roundMoney(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return 0;
  }

  return Math.round(number * 100) / 100;
}

function conversionConfigured(item) {
  return Boolean(
    item?.orderUnit &&
      item?.stockUnit &&
      cleanNumber(item?.stockUnitsPerOrderUnit) > 0 &&
      cleanNumber(item?.salesUnitsPerStockUnit) > 0
  );
}

function stockEquivalent(item) {
  return cleanQuantity(quantities[item.id]) * cleanNumber(item.stockUnitsPerOrderUnit);
}

function displayNumber(value) {
  const number = cleanNumber(value);

  if (!number) {
    return "—";
  }

  return number.toLocaleString("en-GB", {
    maximumFractionDigits: 4,
  });
}

function money(value) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(roundMoney(value));
}

function lineTotal(item) {
  return roundMoney(cleanQuantity(quantities[item.id]) * item.price);
}

function toast(message, classes = "") {
  if (window.M?.toast) {
    window.M.toast({
      html: message,
      classes,
    });
  }
}

/* =========================================================
   Form / modal
   ========================================================= */

function resetQuantities() {
  products.value.forEach((item) => {
    quantities[item.id] = 0;
  });
}

function resetForm() {
  resetQuantities();

  successMessage.value = "";
  errorMessage.value = "";

  closeOverspendModal();
}

function openOverspendModal() {
  overspendMinimumCharacters.value = Math.floor(Math.random() * 5) + 3;

  overspendReason.value = "";

  overspendModalOpen.value = true;
}

function closeOverspendModal() {
  if (submitting.value) {
    return;
  }

  overspendModalOpen.value = false;

  overspendReason.value = "";
}

function buildPayload(overspend = null) {
  return {
    venueId: activeVenueId.value,

    weekEnding: activeWeekEnding.value,

    source: "LiquorOrderingForm.vue",

    supplier: props.supplierName,

    formType: `${props.supplierName} Liquor Order`,

    from_email: FROM_EMAIL,

    supplierToEmail: props.supplierEmail,

    items: selectedItems.value.map((item) => ({
      id: item.id,

      category: item.category,

      product: item.product,

      supplier: item.supplier || props.supplierName,

      /*
       * New conversion fields.
       */
      orderUnit: item.orderUnit,

      stockUnit: item.stockUnit,

      stockUnitsPerOrderUnit: item.stockUnitsPerOrderUnit,

      salesUnitsPerStockUnit: item.salesUnitsPerStockUnit,

      /*
       * Existing backend compatibility fields.
       *
       * unitSize now carries the ORDER unit.
       * stockUnits now carries Stock Units per Order Unit.
       *
       * Both values originate from the Stock Database.
       */
      unitSize: item.orderUnit,

      stockUnits: item.stockUnitsPerOrderUnit,

      unitPrice: item.price,

      quantity: item.quantity,

      lineTotal: item.lineTotal,
    })),

    totals: {
      selectedLines: selectedItems.value.length,

      totalQuantity: totalQuantity.value,

      orderTotal: orderTotal.value,
    },

    overspend: overspend || {
      reason: "",
      minimumCharacters: 0,
    },
  };
}

async function submitOrder() {
  successMessage.value = "";
  errorMessage.value = "";

  if (!activeVenueId.value) {
    errorMessage.value = "No venue selected. Please select a venue before submitting.";

    toast(errorMessage.value, "red darken-2");

    return;
  }

  if (!selectedItems.value.length) {
    errorMessage.value = "Add at least one product quantity before submitting.";

    toast(errorMessage.value, "red darken-2");

    return;
  }

  if (!selectedItems.value.every(conversionConfigured)) {
    errorMessage.value =
      "One or more selected products do not have complete Stock Database conversion values.";

    toast(errorMessage.value, "red darken-2");

    return;
  }

  const freshBudget = await loadBudget();

  if (!freshBudget) {
    errorMessage.value =
      budgetError.value || "The weekly liquor budget could not be checked.";

    toast(errorMessage.value, "red darken-2");

    return;
  }

  if (roundMoney(orderTotal.value) > roundMoney(budget.remainingBudget)) {
    openOverspendModal();

    return;
  }

  await performSubmission(null);
}

async function confirmOverspendSubmission() {
  if (!overspendReasonValid.value || submitting.value) {
    return;
  }

  await performSubmission({
    reason: overspendReason.value.trim(),

    minimumCharacters: overspendMinimumCharacters.value,
  });
}

async function performSubmission(overspend) {
  submitting.value = true;

  successMessage.value = "";
  errorMessage.value = "";

  try {
    const result = await submitLiquorOrderCallable({
      venueId: activeVenueId.value,

      weekEnding: activeWeekEnding.value,

      payload: buildPayload(overspend),
    });

    const orderReference = result?.data?.orderReference || "";

    successMessage.value = orderReference
      ? `${props.supplierName} order submitted for approval. Reference: ${orderReference}`
      : `${props.supplierName} order submitted for approval.`;

    if (result?.data?.emailSent === false && result?.data?.warning) {
      successMessage.value += ` ${result.data.warning}`;
    }

    if (result?.data?.budgetAfter) {
      applyBudget(result.data.budgetAfter);
    } else {
      await loadBudget();
    }

    overspendModalOpen.value = false;

    overspendReason.value = "";

    toast(successMessage.value, "green darken-2");

    resetQuantities();
  } catch (error) {
    console.error(`[LiquorOrderingForm] ${props.supplierName} submission failed:`, error);

    const requiresOverspendReason = Boolean(
      error?.details?.requiresOverspendReason ||
        error?.details?.details?.requiresOverspendReason
    );

    if (requiresOverspendReason) {
      await loadBudget();

      submitting.value = false;

      openOverspendModal();

      return;
    }

    errorMessage.value =
      error?.details?.message ||
      error?.message ||
      "Failed to submit the liquor order. Please try again.";

    toast(errorMessage.value, "red darken-2");
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.liquor-ordering-view {
  margin-top: 18px;
  margin-bottom: 40px;
}

.card-title {
  font-weight: 700;
}

.intro-copy {
  margin-bottom: 18px;
}

.section-heading {
  margin-top: 32px;
  margin-bottom: 16px;
  font-weight: 700;
  color: #00695c;
}

.section-heading.compact {
  margin-top: 0;
}

/* =========================================================
   Budget
   ========================================================= */

.budget-panel {
  margin: 18px 0 22px;
  padding: 18px;
  border: 1px solid #d7e3e1;
  border-radius: 8px;
  background: #f8fbfb;
}

.budget-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.budget-heading h6 {
  margin: 0 0 4px;
  font-size: 1.05rem;
  font-weight: 800;
  color: #004d40;
}

.budget-heading p {
  margin: 0;
  color: #607d7b;
  font-size: 0.9rem;
}

.budget-refresh {
  flex: 0 0 auto;
  margin-top: -5px;
}

.budget-progress {
  margin: 8px 0 0;
}

.budget-error {
  margin: 8px 0 0;
}

.budget-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.budget-tile {
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid #dfe7e6;
  border-radius: 7px;
  background: #ffffff;
}

.budget-tile span {
  display: block;
  margin-bottom: 5px;
  color: #607d7b;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.budget-tile strong {
  display: block;
  color: #263238;
  font-size: 1.28rem;
  line-height: 1.2;
}

.budget-tile.budget-limit {
  border-color: #80cbc4;
  background: #e0f2f1;
}

.budget-tile.budget-positive strong {
  color: #2e7d32;
}

.budget-tile.budget-negative {
  border-color: #ef9a9a;
  background: #ffebee;
}

.budget-tile.budget-negative strong {
  color: #b71c1c;
}

.projected-budget-line {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  background: #edf7f5;
  color: #00695c;
}

.projected-budget-line.overspend {
  background: #ffebee;
  color: #b71c1c;
}

.projected-budget-line span {
  font-weight: 600;
}

.projected-budget-line strong {
  font-size: 1.08rem;
}

.budget-zero-warning {
  margin: 12px 0 0;
  font-size: 0.9rem;
}

/* =========================================================
   Loading / summaries
   ========================================================= */

.loading-panel {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 0;
}

.order-summary-panel {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 12px 0 20px;
}

.summary-tile {
  padding: 14px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fafafa;
}

.summary-tile span {
  display: block;
  margin-bottom: 4px;
  color: #757575;
  font-size: 0.82rem;
}

.summary-tile strong {
  font-size: 1.2rem;
}

.summary-tile.total {
  background: #e0f2f1;
  border-color: #80cbc4;
}

.configuration-warning {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0 0 18px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fff8e1;
  color: #e65100;
  font-size: 0.86rem;
}

.configuration-warning i {
  flex: 0 0 auto;
  font-size: 20px;
}

/* =========================================================
   Category tabs
   ========================================================= */

.category-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

/*
 * These are filter chips rather than normal app buttons,
 * so their sizing/shape is intentionally component-specific.
 */
.category-tab {
  min-height: 36px !important;
  height: 36px !important;
  padding: 0 13px !important;
  border: 1px solid #bdbdbd !important;
  border-radius: 18px !important;
  background: #ffffff !important;
  color: #455a64;
  box-shadow: none !important;
  cursor: pointer;
  font: inherit;
  font-size: 0.9rem !important;
  transition: 0.15s ease;
}

.category-tab:hover {
  border-color: #26a69a !important;
  background: #ffffff !important;
  color: #00695c;
}

.category-tab.active {
  border-color: #00796b !important;
  background: #00796b !important;
  color: #ffffff;
}

/* =========================================================
   Product table
   ========================================================= */

.product-table-wrap,
.selected-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.product-table {
  min-width: 1180px;
}

.selected-table {
  min-width: 760px;
}

.product-table th,
.product-table td,
.selected-table th,
.selected-table td {
  vertical-align: middle;
}

.product-name {
  min-width: 190px;
  font-weight: 600;
}

.category-pill {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  background: #eceff1;
  color: #455a64;
  font-size: 0.78rem;
  white-space: nowrap;
}

.quantity-heading,
.quantity-cell {
  min-width: 110px;
}

.quantity-cell {
  text-align: center;
}

.quantity-input {
  width: 78px !important;
  height: 2.2rem !important;
  margin: 0 !important;
  text-align: center;
}

.line-total {
  min-width: 105px;
  font-weight: 600;
}

.stock-in-value {
  min-width: 100px;
  color: #00695c;
  font-weight: 600;
}

.conversion-missing {
  background: #fffde7 !important;
}

.not-configured {
  color: #c62828;
  font-size: 0.78rem;
  font-weight: 600;
}

.selected-order-panel {
  margin-top: 24px;
  padding: 16px;
  border: 1px solid #d7e3e1;
  border-radius: 6px;
  background: #f8fbfb;
}

.form-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
}

/* =========================================================
   Overspend modal
   ========================================================= */

.overspend-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.52);
}

.overspend-modal-card {
  width: min(680px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 24px;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 12px 42px rgba(0, 0, 0, 0.28);
}

.overspend-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.overspend-modal-header h5 {
  margin: 0 0 6px;
  color: #b71c1c;
  font-weight: 800;
}

.overspend-modal-header p {
  margin: 0;
  color: #616161;
}

/*
 * Icon close control differs from the normal global button.
 */
.overspend-close {
  flex: 0 0 auto;
  min-width: 38px !important;
  width: 38px !important;
  min-height: 38px !important;
  height: 38px !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 50% !important;
  background: transparent !important;
  color: #616161;
  box-shadow: none !important;
  cursor: pointer;
}

.overspend-values {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 20px 0 24px;
}

.overspend-values > div {
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fafafa;
}

.overspend-values span {
  display: block;
  color: #757575;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
}

.overspend-values strong {
  display: block;
  margin-top: 3px;
  font-size: 1.15rem;
}

.overspend-values .overspend-value-red {
  border-color: #ef9a9a;
  background: #ffebee;
  color: #b71c1c;
}

.overspend-reason-field {
  margin-top: 30px;
}

.overspend-reason-field textarea {
  min-height: 110px;
}

.reason-requirement {
  margin: -8px 0 0;
  color: #c62828;
  font-size: 0.86rem;
  font-weight: 600;
}

.reason-requirement.valid {
  color: #2e7d32;
}

.overspend-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

/* =========================================================
   Responsive component-specific layout
   ========================================================= */

@media only screen and (max-width: 992px) {
  .budget-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 600px) {
  /*
   * Container/card/button mobile sizing is already global.
   * These rules only control this component's own structures.
   */

  .budget-heading {
    flex-direction: column;
  }

  .budget-grid,
  .order-summary-panel,
  .overspend-values {
    grid-template-columns: 1fr;
  }

  .projected-budget-line {
    justify-content: space-between;
  }

  .overspend-modal-backdrop {
    padding: 12px;
  }

  .overspend-modal-card {
    padding: 18px;
  }

  .overspend-modal-actions {
    flex-direction: column-reverse;
  }

  .overspend-modal-actions .btn-flat {
    width: 100% !important;
  }
}
</style>
